"use client";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { type Matcher } from "react-day-picker";

import { cn } from "@src/lib/utils/cn";
import { Calendar } from "@src/lib/ui/Calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { selectTriggerClassName } from "@src/lib/ui/Select";

export interface DatePickerProps {
  date?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  defaultMonth?: Date;
  disabled?: Matcher | Matcher[];
  /** Custom trigger element; falls back to the default outline button. */
  trigger?: React.ReactNode;
  /** Dismiss the calendar once a day is picked. Opt-in, since the existing
   * always-open behaviour is load-bearing for inline fields that stay put; set
   * it where the popover would otherwise cover a form's submit button. */
  closeOnSelect?: boolean;
}

export function DatePicker({
  date,
  onSelect,
  placeholder = "Pick a date",
  className,
  defaultMonth,
  disabled,
  trigger,
  closeOnSelect = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger ?? (
          // A field trigger, not an action — style like an input, not a Button.
          <button
            type="button"
            data-empty={!date}
            className={cn(
              selectTriggerClassName,
              "justify-start gap-2 px-2.5 font-normal",
              !date && "text-muted-foreground",
              className,
            )}
          >
            <CalendarIcon className="size-4 shrink-0 text-foreground" />
            {date ? format(date, "MMM d, yyyy") : <span>{placeholder}</span>}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(value) => {
            onSelect?.(value);
            if (closeOnSelect) setOpen(false);
          }}
          initialFocus
          defaultMonth={date || defaultMonth || new Date()}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}
